import css from 'styled-jsx/css';
import { colors, common, zIndex } from '../../theme/theme';

// Toolbar trigger + "choose an action" popover. Button mirrors the
// outlined "Columns" button (.add-column-button) — not a black fill.
export const triggerStyles = css`
  .ba-wrapper {
    margin-right: 16px;
    position: relative;
  }
  .ba-button {
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 26px;
    padding: 0 8px;
    margin: 0;
    white-space: nowrap;
    color: #000;
    border: 2px solid #000;
    border-radius: 8px;
    cursor: pointer;
    background: ${colors.white};
  }
  .ba-button .ba-icon {
    margin-right: 6px;
  }
  .ba-button .ba-caret {
    margin-left: 6px;
    font-size: 10px;
  }
  .ba-panel {
    z-index: ${zIndex.columnSelector};
    position: absolute;
    top: 34px;
    right: 0;
    left: auto;
    width: 360px;
    max-width: calc(100vw - 48px);
    box-sizing: border-box;
    background: ${colors.white};
    border-radius: 12px;
    padding: 12px;
    ${common.secondaryShadow};
  }
  .ba-panel-title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: ${colors.gray400};
    margin: 4px 8px 8px;
  }
  .ba-item {
    display: flex;
    gap: 12px;
    padding: 10px 8px;
    border-radius: 8px;
    cursor: pointer;
  }
  .ba-item:hover {
    background: ${colors.gray100};
  }
  .ba-item.disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .ba-item-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ba-item-icon.blue {
    background: rgba(0, 119, 190, 0.12);
    color: ${colors.interactiveLink};
  }
  .ba-item-icon.red {
    background: rgba(238, 78, 78, 0.12);
    color: ${colors.statusAlert};
  }
  .ba-item-title {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.black};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ba-item-desc {
    font-size: 12px;
    color: ${colors.gray400};
    margin-top: 2px;
  }
  .ba-tag-soon {
    font-size: 10px;
    font-weight: 500;
    color: ${colors.gray400};
    background: ${colors.gray200};
    border-radius: 4px;
    padding: 1px 6px;
  }
  .ba-tag-destructive {
    font-size: 10px;
    font-weight: 600;
    color: ${colors.statusAlert};
    border: 1px solid ${colors.statusAlert};
    border-radius: 4px;
    padding: 1px 6px;
  }
  .icon {
    width: 24px;
    height: 24px;
  }
`;

// Drawer body (3-step wizard) styling. Must be css.global (paired with
// <style jsx global>) — the body is rendered through DrawerLayout's `body`
// prop (separate subtree), so scoped styled-jsx would not match.
export const drawerStyles = css.global`
  .ba-drawer-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    box-sizing: border-box;
    /* bottom clears the absolute footer so content never touches it */
    padding: 24px 28px 96px;
    overflow: hidden;
  }
  /* the active step is the ONLY scroll container; drawer chrome stays fixed */
  .ba-step {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
  .ba-step > div {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* DrawerLayout's shared .body has overflow-y:scroll; while this drawer is
     open, stop the whole panel from scrolling so only .ba-step scrolls. */
  body.ba-drawer-open :global(.ant-drawer-body),
  body.ba-drawer-open :global(.ant-drawer-body > .body) {
    overflow: hidden !important;
  }
  .ba-steps {
    margin: 8px 0 24px;
  }
  /* index.less applies global "body{line-height:1.5!important}" and
     "span{display:flex}" which break antd Steps' own number centering.
     This only neutralizes those globals so antd can center the digit. */
  .ba-steps :global(.ant-steps-icon) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    line-height: 1 !important;
  }
  /* Keep the Intercom launcher behind this drawer while it is open */
  body.ba-drawer-open :global(.intercom-lightweight-app),
  body.ba-drawer-open :global(#intercom-container),
  body.ba-drawer-open :global(.intercom-launcher-frame),
  body.ba-drawer-open :global(iframe[name='intercom-launcher-frame']) {
    z-index: 990 !important;
  }
  /* The shared ConfirmModal (DrawerModal) hard-codes its mask to the
     standard 435px right drawer. While the wider bulk drawer is open,
     widen the mask to the bulk drawer width so the confirm centers over
     it. Scoped to ba-drawer-open only — other confirms are unaffected. */
  body.ba-drawer-open :global(.drawer-modal-wrapper .mask) {
    width: 920px !important;
  }
  .ba-action-banner {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    background: ${colors.gray100};
    border: 1px solid ${colors.gray200};
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 20px;
  }
  .ba-action-banner .ba-banner-title {
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 4px;
  }
  .ba-action-banner .ba-banner-desc {
    font-size: 13px;
    color: ${colors.gray400};
  }
  .ba-dropzone {
    border: 2px dashed ${colors.grey};
    border-radius: 12px;
    padding: 64px 24px;
    text-align: center;
    cursor: pointer;
    background: ${colors.gray100};
  }
  .ba-dropzone:hover {
    border-color: ${colors.interactiveLink};
  }
  .ba-dropzone .anticon {
    font-size: 28px;
    color: ${colors.interactiveLink};
  }
  .ba-dropzone-title {
    font-size: 18px;
    font-weight: 700;
    margin: 16px 0 6px;
  }
  .ba-dropzone-hint {
    font-size: 13px;
    color: ${colors.gray400};
  }
  .ba-file-chip {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid ${colors.statusSuccess};
    background: rgba(10, 183, 53, 0.06);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
  }
  .ba-file-chip .ba-file-name {
    font-weight: 600;
  }
  .ba-file-chip .ba-file-meta {
    font-size: 12px;
    color: ${colors.gray400};
  }
  .ba-file-chip .ba-file-remove {
    margin-left: auto;
    cursor: pointer;
    color: ${colors.gray400};
  }
  .ba-stat-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    border: 1px solid ${colors.gray200};
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
  }
  .ba-stat-grid.three {
    grid-template-columns: repeat(3, 1fr);
  }
  .ba-stat {
    padding: 18px 22px;
    border-right: 1px solid ${colors.gray200};
  }
  .ba-stat:last-child {
    border-right: 0;
  }
  .ba-stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: ${colors.gray400};
  }
  .ba-stat-value {
    font-size: 26px;
    font-weight: 700;
    margin-top: 6px;
  }
  .ba-stat-value.green {
    color: ${colors.statusSuccess};
  }
  .ba-stat-value.red {
    color: ${colors.statusAlert};
  }
  .ba-label {
    font-size: 13px;
    font-weight: 600;
    margin: 16px 0 6px;
  }
  .ba-help {
    font-size: 12px;
    color: ${colors.gray400};
    margin-top: 6px;
  }
  .ba-template-row {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }
  .ba-column-error {
    margin-top: 8px;
    padding: 10px 12px;
    border: 1px solid ${colors.statusAlert};
    background: #fff1f0;
    color: ${colors.statusAlert};
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.4;
  }
  .ba-samples {
    border: 1px solid ${colors.gray200};
    border-radius: 10px;
    overflow: hidden;
    margin-top: 8px;
  }
  .ba-sample-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid ${colors.gray200};
    font-family: monospace;
    font-size: 13px;
  }
  .ba-sample-row:last-child {
    border-bottom: 0;
  }
  .ba-sample-row .ba-sample-idx {
    color: ${colors.gray400};
  }
  .ba-sample-more {
    padding: 10px 16px;
    font-size: 12px;
    color: ${colors.gray400};
    background: ${colors.gray100};
  }
  .ba-notice {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    background: rgba(245, 166, 35, 0.1);
    border: 1px solid rgba(245, 166, 35, 0.4);
    border-radius: 10px;
    padding: 14px 18px;
    margin: 16px 0;
  }
  .ba-notice .ba-notice-text {
    font-size: 13px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .ba-error-box {
    background: rgba(238, 78, 78, 0.06);
    border: 1px solid rgba(238, 78, 78, 0.3);
    border-radius: 10px;
    padding: 18px 20px;
    margin-top: 20px;
  }
  .ba-error-box .ba-error-title {
    font-weight: 700;
    margin-bottom: 6px;
  }
  .ba-error-box .ba-error-desc {
    font-size: 13px;
    color: ${colors.gray400};
    margin-bottom: 14px;
  }
  .ba-preview-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .ba-preview-title {
    font-size: 18px;
    font-weight: 700;
  }
  .ba-preview-sub {
    font-size: 13px;
    color: ${colors.gray400};
    margin-top: 2px;
  }
  .ba-result-head {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-bottom: 22px;
  }
  .ba-result-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(245, 166, 35, 0.15);
    color: ${colors.statusProgress};
    font-size: 24px;
  }
  .ba-result-icon.ok {
    background: rgba(10, 183, 53, 0.15);
    color: ${colors.statusSuccess};
  }
  .ba-result-title {
    font-size: 24px;
    font-weight: 700;
  }
  .ba-result-sub {
    font-size: 14px;
    color: ${colors.gray400};
    margin-top: 4px;
  }
  .ba-csv-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid ${colors.gray200};
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 13px;
    color: ${colors.black};
    background: ${colors.white};
    cursor: pointer;
    white-space: nowrap;
    flex: 0 0 auto;
  }
  .ba-tag-destructive {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.statusAlert};
    border: 1px solid ${colors.statusAlert};
    border-radius: 4px;
    padding: 1px 8px;
    white-space: nowrap;
  }
  .ba-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 28px;
    border-top: 1px solid ${colors.gray200};
    background: ${colors.white};
  }
  .ba-footer .ba-footer-info {
    font-size: 13px;
    color: ${colors.gray400};
  }
  .ba-footer .ba-footer-actions {
    display: flex;
    gap: 12px;
  }
  .ba-table-wrap {
    border: 1px solid ${colors.gray200};
    border-radius: 10px;
    overflow: hidden;
    flex: 1 1 auto;
    /* keep the table usable; below this the whole step scrolls instead
       of the table collapsing into the footer on short screens */
    min-height: 240px;
    display: flex;
    flex-direction: column;
  }
  /* PreviewStep renders a plain antd Table whose scroll.y is sized by JS to
     this wrapper, so antd handles its own sticky header + body scroll. We
     only let the wrapper flex and lighten the pager bar. */
  .ba-table-wrap :global(.ant-spin-nested-loading),
  .ba-table-wrap :global(.ant-spin-container),
  .ba-table-wrap :global(.ant-table-wrapper) {
    flex: 1 1 auto;
    min-height: 0;
  }
  .ba-table-wrap :global(.ant-pagination) {
    background: ${colors.gray100};
    margin: 0;
    padding: 10px 14px;
  }
  .ba-table-wrap :global(.ant-table-row:hover) {
    background: linear-gradient(
        ${colors.highlight.hovered},
        ${colors.highlight.hovered}
      ),
      #fff !important;
  }
  .ba-table-wrap :global(.ant-table-row .ant-table-cell-fix-left),
  .ba-table-wrap :global(.ant-table-row .ant-table-cell-fix-left-last) {
    background: #fff !important;
  }
  .ba-table-wrap :global(.ant-table-row:hover .ant-table-cell-fix-left),
  .ba-table-wrap :global(.ant-table-row:hover .ant-table-cell-fix-left-last) {
    background: linear-gradient(
        ${colors.highlight.hovered},
        ${colors.highlight.hovered}
      ),
      #fff !important;
  }
`;
